import { useCallback } from 'react'
import { Card } from '@/common/ui/Card/Card'
import { Checkbox } from '@/common/ui/Form/Checkbox/checkbox'
import { Button } from '@/common/ui/Form/Button/Button'
import { cn } from '@/common/lib/cn/cn'
import type { DetailedBrandOption } from '../../lib/detailedBrandComparison'
import { getTopBrandsByVolume } from '../../lib/detailedBrandComparison'
import type { CompanyStatsComparisonMode } from '../../model/useCompanyStatsBrandComparison'

type CompanyStatsDetailedBrandComparisonBrandSelectorProps = {
    compareBy: CompanyStatsComparisonMode
    brandOptions: DetailedBrandOption[]
    selectedBrandIds: string[]
    onSelectionChange: (ids: string[]) => void
    isLoading?: boolean
}

export const CompanyStatsDetailedBrandComparisonBrandSelector = ({
    compareBy,
    brandOptions,
    selectedBrandIds,
    onSelectionChange,
    isLoading = false,
}: CompanyStatsDetailedBrandComparisonBrandSelectorProps) => {
    const selectedSet = new Set(selectedBrandIds)
    const entityPluralLabel = compareBy === 'brand' ? 'Бренды' : 'Продавцы'
    const entitySingularLabel = compareBy === 'brand' ? 'бренд' : 'продавца'

    const handleToggle = useCallback(
        (brandId: string) => {
            onSelectionChange(
                selectedBrandIds.includes(brandId)
                    ? selectedBrandIds.filter(id => id !== brandId)
                    : [...selectedBrandIds, brandId]
            )
        },
        [onSelectionChange, selectedBrandIds]
    )

    const handleSelectAll = useCallback(() => {
        onSelectionChange(brandOptions.map(brand => brand.id))
    }, [brandOptions, onSelectionChange])

    const handleClearSelection = useCallback(() => {
        onSelectionChange([])
    }, [onSelectionChange])

    const handleTop10 = useCallback(() => {
        onSelectionChange(getTopBrandsByVolume(brandOptions, 10))
    }, [brandOptions, onSelectionChange])

    return (
        <Card color='primary'>
            <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <h3 className='text-xs font-bold uppercase tracking-wide text-base-content/80'>
                    {entityPluralLabel} в сравнении · {selectedBrandIds.length} из {brandOptions.length}
                </h3>
                <div className='flex flex-wrap gap-2'>
                    <Button type='button' emphasis='secondary' size='sm' onClick={handleSelectAll}>
                        Выбрать все
                    </Button>
                    <Button type='button' emphasis='secondary' size='sm' onClick={handleClearSelection}>
                        Снять выбор
                    </Button>
                    <Button type='button' emphasis='secondary' size='sm' onClick={handleTop10}>
                        Топ-10 по объёму
                    </Button>
                </div>
            </div>

            {isLoading && brandOptions.every(brand => brand.productCount === 0) ? (
                <div className='flex items-center justify-center py-8'>
                    <span className='loading loading-spinner loading-md' aria-label='Загрузка брендов' />
                </div>
            ) : (
                <div className='mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7'>
                    {brandOptions.map(brand => {
                        const isSelected = selectedSet.has(brand.id)
                        return (
                            <label
                                key={brand.id}
                                className={cn(
                                    'flex cursor-pointer items-center gap-2 rounded-lg border px-2 py-2 text-sm transition-colors',
                                    isSelected
                                        ? 'border-white/20 bg-white/10'
                                        : 'border-transparent bg-white/5 opacity-60 hover:opacity-80'
                                )}
                            >
                                <Checkbox
                                    size='sm'
                                    checked={isSelected}
                                    onChange={() => handleToggle(brand.id)}
                                    aria-label={`Выбрать ${entitySingularLabel} ${brand.name}`}
                                />
                                <span
                                    className='inline-block h-2 w-2 shrink-0 rounded-full'
                                    style={{ backgroundColor: brand.color }}
                                    aria-hidden
                                />
                                <span className='min-w-0 flex-1 truncate'>{brand.name}</span>
                                <span className='shrink-0 tabular-nums text-xs text-base-content/60'>
                                    {brand.productCount}
                                </span>
                            </label>
                        )
                    })}
                </div>
            )}
        </Card>
    )
}
